import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';
import { studentAPI, paymentAPI } from '@/services/api';
import { getStatusColor, getGradeColor } from '@/utils/helpers';

const StatCard = ({ icon, label, value, color, link }) => (
  <Link to={link} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md hover:-translate-y-0.5 transition-all group">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${color} bg-opacity-10`}>
      <i className={`fas ${icon} text-xl ${color.replace('bg-', 'text-')}`} />
    </div>
    <h3 className="text-2xl font-black text-slate-800 mb-1">{value}</h3>
    <p className="text-slate-500 text-sm font-medium">{label}</p>
  </Link>
);

const QuickAction = ({ icon, title, description, link, color }) => (
  <Link
    to={link}
    className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-slate-100 hover:shadow-lg hover:-translate-y-0.5 transition-all group"
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <i className={`fas ${icon} text-white text-lg`} />
    </div>
    <div>
      <h4 className="font-bold text-slate-800 group-hover:text-emerald-600 transition-colors">{title}</h4>
      <p className="text-sm text-slate-500 mt-1">{description}</p>
    </div>
    <i className="fas fa-chevron-right text-slate-300 group-hover:text-emerald-500 ml-auto self-center transition-colors" />
  </Link>
);

const StudentDashboardHome = () => {
  const { user } = useAuth();
  const student = user?.user || {};
  const [results, setResults] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resultsRes, paymentsRes] = await Promise.all([
          studentAPI.getResults(),
          paymentAPI.getMyPayments()
        ]);
        setResults(resultsRes.data.data || []);
        setPayments(paymentsRes.data.data || []);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const latestResults = results.slice(0, 4);
  const pendingPayments = payments.filter((p) => p.status === 'pending');
  const paidTotal = payments
    .filter((p) => p.status === 'paid')
    .reduce((s, p) => s + Number(p.amount), 0);
  const pendingTotal = payments
    .filter((p) => p.status === 'pending')
    .reduce((s, p) => s + Number(p.amount), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
        <div className="relative z-10">
          <h1 className="text-3xl font-black mb-2">Welcome back, {student.full_name || 'Student'}! 👋</h1>
          <p className="text-emerald-100 text-lg">
            Here's what's happening with your academic journey today.
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          icon="fa-check-circle"
          label="Payment Status"
          value={student.payment_status || 'N/A'}
          color="bg-blue-500 text-blue-600"
          link="/student/payments"
        />
        <StatCard
          icon="fa-chalkboard"
          label="Current Class"
          value={student.class || 'N/A'}
          color="bg-amber-500 text-amber-600"
          link="/student/subjects"
        />
        <StatCard
          icon="fa-book"
          label="Results"
          value={results.length}
          color="bg-purple-500 text-purple-600"
          link="/student/results"
        />
        <StatCard
          icon="fa-credit-card"
          label="Payments"
          value={payments.length}
          color="bg-emerald-500 text-emerald-600"
          link="/student/payments"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <div className="lg:col-span-2 space-y-5">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <i className="fas fa-bolt text-amber-500" /> Quick Actions
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <QuickAction
              icon="fa-award"
              title="View Results"
              description="Check your latest academic performance"
              link="/student/results"
              color="bg-gradient-to-br from-emerald-500 to-emerald-700"
            />
            <QuickAction
              icon="fa-credit-card"
              title="Make Payment"
              description={pendingPayments.length > 0 ? `${pendingPayments.length} pending payment(s)` : 'All payments up to date'}
              link="/student/payments"
              color="bg-gradient-to-br from-blue-500 to-blue-700"
            />
            <QuickAction
              icon="fa-user-edit"
              title="Update Profile"
              description="Manage your personal information"
              link="/student/profile"
              color="bg-gradient-to-br from-purple-500 to-purple-700"
            />
            <QuickAction
              icon="fa-bell"
              title="Notifications"
              description="No new notifications"
              link="/student/notifications"
              color="bg-gradient-to-br from-amber-500 to-amber-700"
            />
          </div>

          {/* Recent Results Preview */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <i className="fas fa-chart-bar text-emerald-500" /> Recent Results
              </h3>
              <Link to="/student/results" className="text-sm font-medium text-emerald-600 hover:text-emerald-700">
                View All <i className="fas fa-arrow-right ml-1" />
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {latestResults.length > 0 ? latestResults.map((result, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                      <i className="fas fa-book" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{result.subject}</p>
                      <p className="text-xs text-slate-500">Total: {result.total}/100</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${getGradeColor(result.grade)}`}>
                    {result.grade}
                  </span>
                </div>
              )) : (
                <div className="p-8 text-center text-slate-400 text-sm">No results available yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-5">
          {/* Payment Summary */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
              <i className="fas fa-wallet text-blue-500" /> Payment Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total Paid</span>
                <span className="font-bold text-emerald-600">
                  ₦{paidTotal.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Pending</span>
                <span className="font-bold text-amber-600">
                  ₦{pendingTotal.toLocaleString()}
                </span>
              </div>
              <div className="pt-3 border-t border-slate-100">
                <Link
                  to="/student/payments"
                  className="block w-full text-center py-2.5 bg-emerald-50 text-emerald-700 rounded-xl text-sm font-semibold hover:bg-emerald-100 transition-colors"
                >
                  View Details
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboardHome;

